import { openDialog } from './dialog.js';
import { dayKey, isSiteVisit } from '../tasks.js';

// PUT IT IN THE DIARY — a task's visit onto the shared Sigma calendar.
//
// v2.28.0 built this on the turn-3 Today row. Turns 7/8 deleted that row, and
// the button went with it, so from 1 Sep nothing on the Tasks page could put a
// site visit in the diary at all (Karan, 05/10: "notifications/calendar don't
// happen"). It now lives in the task panel, which every view opens.
//
// The write is the same one the Calendar screen and leave approval make: the
// calendarInvite function, acting AS THE CALLER, on the calendar the caller
// names. The time is asked for, because it is the one thing a task cannot know.

const pad = (n) => String(n).padStart(2, '0');
const local = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:00`;

/** Offer the diary for this task? A visit by its words, or one already in it. */
export const wantsDiary = (t) => !!t && t.state !== 'done' && (isSiteVisit(t) || !!t.diaryAt);

/**
 * The calendarInvite payload, and what goes back on the task. Pure.
 * v: { date 'YYYY-MM-DD', start 'HH:MM', hours }.
 */
export function diaryEvent(task = {}, project = null, v = {}, calendarId) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v.date || '')) throw new Error('Pick the day.');
  if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(v.start || '')) throw new Error('Give the start time, 24-hour HH:MM.');
  const startDt = new Date(`${v.date}T${v.start.length === 4 ? '0' + v.start : v.start}:00`);
  const hours = Number(v.hours) > 0 ? Number(v.hours) : 1;
  const endDt = new Date(startDt.getTime() + hours * 3600 * 1000);
  const number = project && project.number ? String(project.number) : '';
  const client = project ? (project.clientName || project.title || '') : '';
  const site = project && project.siteAddress
    ? [project.siteAddress.line1, project.siteAddress.town, project.siteAddress.postcode].filter(Boolean).join(', ') : '';
  const who = String(task.assigneeEmail || '').toLowerCase();
  return {
    call: {
      summary: `SITE: ${number ? number + ' / ' : ''}${client ? client + ' — ' : ''}${task.title || 'Site visit'}`.slice(0, 300),
      description: `From Track — task "${task.title || ''}".`,
      location: site || undefined,
      start: local(startDt), end: local(endDt),
      attendees: who ? [who] : [],
      calendar: 'sigma', calendarId, source: 'diary',
    },
    patch: { diaryAt: `${v.date.slice(8, 10)}/${v.date.slice(5, 7)} ${v.start}`, diaryDate: v.date },
  };
}

/**
 * @param opts.task, opts.project
 * @param opts.onSaved  async (patch) => void — writes diaryAt (+ sigmaEventId) on the task
 */
export function putInDiary({ task, project = null, onSaved }) {
  return openDialog({
    title: 'Put it in the diary',
    subtitle: 'On the shared Sigma calendar, with the assignee invited',
    width: 520,
    fields: [
      { key: 'date', label: 'Day', type: 'date', required: true,
        value: task.diaryDate || task.plannedDate || task.dueDate || dayKey() },
      { key: 'start', label: 'Start', type: 'time', required: true, placeholder: '10:00' },
      { key: 'hours', label: 'How long (hours)', type: 'number', optional: true, value: '2' },
    ],
    primary: {
      label: 'Add to Sigma',
      onSubmit: async (v) => {
        const [{ listCalendars }, { getFunctions, httpsCallable }, { app }] = await Promise.all([
          import('../google-api.js'),
          import('../fb/functions.js'),
          import('../firebase.js'),
        ]);
        const cals = await listCalendars();
        const sigma = cals.find((c) => /sigma/i.test(c.summary || ''));
        if (!sigma) throw new Error('Could not find the Sigma calendar in your Google account. Ask for access to Sigma.');
        const { call, patch } = diaryEvent(task, project, v, sigma.id);
        const invite = httpsCallable(getFunctions(app, 'europe-west2'), 'calendarInvite');
        const res = await invite(call);
        await onSaved({ ...patch, sigmaEventId: (res && res.data && res.data.id) || null });
        // MOVING, NOT DUPLICATING. The new entry is written first, so a failed
        // removal leaves two entries rather than none.
        if (task.sigmaEventId) {
          await invite({ action: 'delete', eventId: task.sigmaEventId, calendar: 'sigma', calendarId: sigma.id, source: 'diary' })
            .catch((e) => console.warn('[diary] the old entry is still on Sigma:', e && e.message));
        }
      },
    },
  });
}
